import { FC, useCallback } from 'react';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Button,
} from '@ibsys/kit';
import { ArrowLeftIcon } from 'lucide-react';

import { useTranslation } from 'react-i18next';
import { DialogDataSection } from '@/features/DialogDataSection';
import { DialogTranscriptSection } from '@/features/DialogTranscriptSection';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMainLayout } from '@/shared/ui';
import { useDialogsSectionRoute } from '../model/useDialogsSectionRoute';

type SessionDetailsProps = {
  id: number;
};

export const SessionDetails: FC<SessionDetailsProps> = ({ id }) => {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? undefined;
  const panel = searchParams.get('panel') ?? 'data';
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMainLayout();
  const { t } = useTranslation('dialogs');
  const { listPath, buildRecordPath } = useDialogsSectionRoute();

  const handleBack = () => {
    // Источник перехода запоминается в state как полный путь возврата (дашборд,
    // выборка, список диалогов). Его нет — возвращаемся к списку текущего раздела.
    navigate(typeof location.state === 'string' ? location.state : listPath);
  };

  const handleTabChange = useCallback(
    (newTab: string) => {
      const params = new URLSearchParams(searchParams);
      params.set('tab', newTab);
      navigate(`${buildRecordPath(String(id))}?${params.toString()}`, { replace: true });
    },
    [id, navigate, searchParams, buildRecordPath],
  );

  const handlePanelChange = useCallback(
    (newPanel: string) => {
      const params = new URLSearchParams(searchParams);
      params.set('panel', newPanel);
      navigate(`${buildRecordPath(String(id))}?${params.toString()}`, { replace: true });
    },
    [id, navigate, searchParams, buildRecordPath],
  );

  if (!id) return <div />;

  if (isMobile) {
    return (
      <Tabs
        value={panel}
        onValueChange={handlePanelChange}
        className="grid grid-rows-[auto_1fr] grid-cols-1 h-full gap-3 min-h-0"
      >
        <div className={'flex gap-2 items-center'}>
          <Button variant={'link'} className={'p-1'} onClick={handleBack}>
            <ArrowLeftIcon className={'size-5'} />
          </Button>
          <TabsList>
            <TabsTrigger value="data">{t('dialogData')}</TabsTrigger>
            <TabsTrigger value="transcript">{t('dialog')}</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="data" className="overflow-hidden">
          <DialogDataSection
            dialogId={id}
            hideBackButton
            onBack={handleBack}
            activeTab={tab}
            onTabChange={handleTabChange}
          />
        </TabsContent>
        <TabsContent value="transcript" className="flex-1 overflow-hidden">
          <DialogTranscriptSection dialogId={id} showAudio isCollapsible={false} />
        </TabsContent>
      </Tabs>
    );
  }

  return (
    <ResizablePanelGroup direction="horizontal" className={'gap-3 h-full'} storageName={'dialogs-page-advanced'}>
      <ResizablePanel className={'p-1'} id={'data'}>
        <DialogDataSection dialogId={id} onBack={handleBack} activeTab={tab} onTabChange={handleTabChange} />
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel className={'p-1'} id={'chat'} groupResizeBehavior={'preserve-relative-size'} defaultSize={'33%'}>
        <DialogTranscriptSection dialogId={id} showAudio={false} isCollapsible={false} />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
};
